import { ExternalLinkIcon, FileTextIcon, SearchIcon } from 'lucide-react'
import {
  type LakeSearchDocumentsByNameArgs,
  type LakeSearchDocumentsByNameResult,
  normalizeDisplayFilename,
} from '@/features/agent/tools/lake-search-documents-by-name'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { cn } from '@/utils/cn'
import { HIT_NAME_ROOM } from './_shared/hit-row'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface LakeSearchDocumentsByNameViewProps {
  args: LakeSearchDocumentsByNameArgs
  parsed: LakeSearchDocumentsByNameResult | null
  running: boolean
  errored: boolean
  /** Open a hit's lake document detail sheet. Omitted/no-op disables the link
   *  affordance is still driven by `hit.document_id`. */
  onOpenDocument?: (documentId: string) => void
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

/** Pure presentation for a `lake_search_documents_by_name` tool-result card. */
export function LakeSearchDocumentsByNameView({
  args,
  parsed,
  running,
  errored,
  onOpenDocument,
  defaultOpen = false,
}: LakeSearchDocumentsByNameViewProps) {
  const hits = parsed?.documents ?? []
  const total = parsed?.total ?? hits.length
  const hasBody = !running && (errored || hits.length > 0)
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, errored)

  const headerStatus: ToolCallStatus = running ? 'running' : errored ? 'error' : 'complete'

  const label = running
    ? 'Looking up filename for'
    : errored
      ? 'Failed —'
      : total === 0
        ? 'No filename match for'
        : `${total} filename match${total === 1 ? '' : 'es'} for`

  const description = (
    <>
      {label}{' '}
      <code className="ml-0.5 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
        "{args.name}"
      </code>
    </>
  )

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={SearchIcon}
        toolName="lake_search_documents_by_name"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="flex flex-col gap-2 border-t border-border px-3 py-2">
          {parsed?.error && (
            <p className="whitespace-pre-wrap text-xs text-muted-foreground">{parsed.error}</p>
          )}
          {hits.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {hits.map((hit, i) => {
                const canOpen = Boolean(hit.document_id)
                const body = (
                  <div className="flex items-start gap-2">
                    <FileTextIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                    <div className="flex min-w-0 flex-col gap-1 max-md:flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 text-xs">
                        <span
                          className={cn(
                            'truncate font-medium',
                            HIT_NAME_ROOM.icon,
                            canOpen && 'text-primary underline-offset-2 group-hover:underline'
                          )}
                        >
                          {normalizeDisplayFilename(hit.filename)}
                        </span>
                        {canOpen && (
                          <ExternalLinkIcon className="size-3 shrink-0 text-muted-foreground" />
                        )}
                        {hit.label && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                            {hit.label}
                          </span>
                        )}
                      </div>
                      {hit.storage_path && (
                        <p className="truncate text-[11px] text-muted-foreground/80">
                          {hit.storage_path}
                        </p>
                      )}
                    </div>
                  </div>
                )
                return (
                  <li key={hit.document_id || i}>
                    {canOpen ? (
                      <button
                        type="button"
                        onClick={() => onOpenDocument?.(hit.document_id)}
                        className="group w-full cursor-pointer rounded-md border border-border px-2.5 py-2 text-left transition-colors hover:border-primary hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        {body}
                      </button>
                    ) : (
                      <div className="rounded-md border border-border px-2.5 py-2">{body}</div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}
